import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, Edit3, Trash2, Calendar, User, Package } from "lucide-react";
import AuditLine from "../components/AuditLine";

/**
 * 📤 Preluat din Inventar
 *
 * Modul care afișează istoricul tuturor preluărilor făcute de angajați din inventar
 * (stocul scade automat la preluare prin /api/inventory/{id}/issue).
 *
 * - Admin vede tot, cu opțiune de filtru per angajat.
 * - User normal vede doar preluările proprii.
 * - Click „Editează" pe orice rând permite modificarea cantității + notă + proiect.
 *   Stocul se ajustează automat în consecință (delta vechi/nou).
 * - Admin poate șterge o preluare (returnează cantitatea în stoc).
 */
export default function PreluatDinInventar() {
  const { user, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterUser, setFilterUser] = useState("__all__");
  const [filterItem, setFilterItem] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = isAdmin ? "/inventory/issuances" : "/inventory/issuances/me";
      const params = [];
      if (isAdmin && filterUser !== "__all__") params.push(`user_id=${encodeURIComponent(filterUser)}`);
      if (filterItem) params.push(`item_id=${encodeURIComponent(filterItem)}`);
      const full = url + (params.length ? `?${params.join("&")}` : "") + (params.length ? "&" : "?") + "limit=500";
      const { data } = await api.get(full);
      setRows(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Eroare la încărcarea preluărilor");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, filterItem]);

  // Listă unică de angajați care au făcut preluări (pentru dropdown filtru, doar admin)
  const uniqueUsers = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (r.user_id && !map.has(r.user_id)) map.set(r.user_id, r.user_name || r.user_id); });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  // Filtrare după search text (nume produs sau angajat)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.item_name || ""} ${r.user_name || ""} ${r.note || ""} ${r.project_name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  // Total: sumă cantități per item (pentru rezumat sus)
  const totalByItem = useMemo(() => {
    const m = new Map();
    filtered.forEach((r) => {
      const key = r.item_name || "—";
      const e = m.get(key) || { name: key, total: 0, unit: r.item_unit || "buc", count: 0 };
      e.total += parseFloat(r.quantity) || 0;
      e.count += 1;
      m.set(key, e);
    });
    return Array.from(m.values()).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [filtered]);

  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = d.getFullYear();
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${yy} · ${HH}:${MM}`;
    } catch { return iso; }
  };

  const openEdit = (row) => {
    setEditing({
      id: row.id,
      quantity: String(row.quantity || 0),
      note: row.note || "",
      project_id: row.project_id || "",
      item_name: row.item_name,
      item_unit: row.item_unit || "buc",
      user_name: row.user_name,
      ts: row.ts,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const qty = parseFloat(editing.quantity);
    if (!qty || qty <= 0) { toast.error("Cantitatea trebuie să fie pozitivă"); return; }
    setBusy(true);
    try {
      await api.put(`/inventory/issuances/${editing.id}`, {
        quantity: qty,
        note: editing.note.trim() || null,
        project_id: editing.project_id || null,
      });
      toast.success("Preluare actualizată — stocul a fost ajustat");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Sigur anulezi preluarea de ${row.quantity} ${row.item_unit || "buc"} din "${row.item_name}"?\n\nCantitatea va fi returnată în stoc.`)) return;
    try {
      await api.delete(`/inventory/issuances/${row.id}`);
      toast.success("Preluare anulată — stocul restaurat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  return (
    <div className="p-2" data-testid="page-preluat-din-inventar">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">📤 Preluat din Inventar</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Istoric complet al preluărilor: ce a luat fiecare angajat, când și cât.{" "}
            <strong>Stocul se scade automat</strong> la fiecare preluare.
          </p>
        </div>
      </div>

      {/* Filtre */}
      <div className="card-flat p-3 mb-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            placeholder="Caută după produs, angajat sau notă…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 text-sm"
            data-testid="filter-search"
          />
        </div>
        {isAdmin && (
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="h-9 w-[220px] text-sm" data-testid="filter-user">
              <SelectValue placeholder="Toți angajații"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">— Toți angajații —</SelectItem>
              {uniqueUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="text-xs text-zinc-500">
          <strong className="text-zinc-800 tabular-nums">{filtered.length}</strong>{" "}
          {filtered.length === 1 ? "preluare" : "preluări"} afișate
        </div>
      </div>

      {/* Top 5 cele mai preluate */}
      {totalByItem.length > 0 && (
        <div className="card-flat p-3 mb-3" data-testid="top-items">
          <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-2">📊 Top 5 cele mai preluate</div>
          <div className="flex flex-wrap gap-2">
            {totalByItem.map((t) => (
              <div key={t.name} className="px-2 py-1 rounded bg-zinc-100 border border-zinc-300 text-xs">
                <strong>{t.name}</strong>{" "}
                <span className="text-zinc-600 tabular-nums">— {t.total} {t.unit}</span>{" "}
                <span className="text-zinc-400 text-[10px]">({t.count}× pe lista filtrată)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabel preluări */}
      <div className="card-flat overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500 italic">Se încarcă…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500 italic" data-testid="empty-state">
            Nicio preluare găsită. Când angajații vor folosi „Preia din magazie" în pagina Inventar, înregistrările vor apărea aici.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2"><Calendar size={11} className="inline mr-1"/>Data / Ora</th>
                  <th className="px-3 py-2"><User size={11} className="inline mr-1"/>Angajat</th>
                  <th className="px-3 py-2"><Package size={11} className="inline mr-1"/>Produs</th>
                  <th className="px-3 py-2 text-right">Cantitate</th>
                  <th className="px-3 py-2">Proiect</th>
                  <th className="px-3 py-2">Notă</th>
                  <th className="px-3 py-2 w-[90px]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const canEdit = isAdmin || r.user_id === user?.id;
                  return (
                    <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs tabular-nums">{fmtDateTime(r.ts)}</td>
                      <td className="px-3 py-2 font-semibold">
                        {r.user_name || "—"}
                        <AuditLine entry={r} testid={`audit-${r.id}`} compact />
                      </td>
                      <td className="px-3 py-2">{r.item_name || "—"}</td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">
                        {r.quantity} <span className="text-[10px] text-zinc-500 font-normal">{r.item_unit || "buc"}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700">{r.project_name || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-zinc-700 max-w-[260px] truncate" title={r.note || ""}>{r.note || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 text-right">
                        {canEdit && (
                          <button
                            onClick={() => openEdit(r)}
                            className="text-blue-600 hover:bg-blue-50 p-1.5 rounded"
                            title="Editează preluarea"
                            data-testid={`edit-${r.id}`}
                          >
                            <Edit3 size={14}/>
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => deleteRow(r)}
                            className="text-red-600 hover:bg-red-50 p-1.5 rounded ml-1"
                            title="Anulează (returnează stocul)"
                            data-testid={`delete-${r.id}`}
                          >
                            <Trash2 size={14}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v && !busy) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>✏️ Editează preluare</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div className="card-flat p-3 bg-zinc-50 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Produs</span>
                  <strong>{editing.item_name}</strong>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Preluat de</span>
                  <strong>{editing.user_name}</strong>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">La data</span>
                  <strong className="font-mono">{fmtDateTime(editing.ts)}</strong>
                </div>
              </div>
              <div>
                <Label>Cantitate ({editing.item_unit})</Label>
                <Input
                  type="number" min={0.01} step={0.01}
                  value={editing.quantity}
                  onChange={(e) => setEditing({ ...editing, quantity: e.target.value })}
                  data-testid="edit-qty"
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  ⚠ Schimbarea cantității ajustează automat stocul (diferența +/− e mutată din/în inventar).
                </div>
              </div>
              <div>
                <Label>Proiect (opțional)</Label>
                <Select
                  value={editing.project_id || "__none__"}
                  onValueChange={(v) => setEditing({ ...editing, project_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Niciunul —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title || p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notă</Label>
                <Textarea
                  rows={2}
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  data-testid="edit-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Anulează</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-[var(--primary)]" data-testid="save-edit-btn">
              {busy ? "Se salvează…" : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
